import type { WebKilnProject } from '../types';
import { extractProjectMarkup, sanitizeHtml } from '../../worker/src/publishing';

export interface SiteCheck {
  pageId: string;
  pageName: string;
  detail: string;
}
export async function inspectSite(project: WebKilnProject): Promise<SiteCheck[]> {
  const issues: SiteCheck[] = [];
  for (const page of project.pages) {
    const report = (detail: string) =>
      issues.push({ pageId: page.id, pageName: page.name, detail });
    const markup = extractProjectMarkup(page.projectData);
    const doc = new DOMParser().parseFromString(sanitizeHtml(markup.html), 'text/html');
    if (!page.seo?.title?.trim()) report('Add an SEO title in Page settings.');
    if (!page.seo?.description?.trim()) report('Add an SEO description in Page settings.');
    if (!doc.querySelector('h1')) report('Add a main heading (H1).');
    for (const link of doc.querySelectorAll<HTMLAnchorElement>('a[href]')) {
      const href = link.getAttribute('href') ?? '';
      if (
        href.startsWith('/') &&
        !href.startsWith('//') &&
        !project.pages.some((item) => item.slug === href.split(/[?#]/)[0])
      )
        report(`Broken internal link: ${href}`);
      if (href.startsWith('#') && href.length > 1 && !doc.getElementById(href.slice(1)))
        report(`Missing anchor target: ${href}`);
    }
    for (const image of doc.querySelectorAll('img'))
      if (!image.getAttribute('alt')?.trim()) report('An image needs descriptive alt text.');
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-same-origin');
    frame.className = 'site-check-frame';
    frame.title = 'Mobile layout check';
    frame.srcdoc = `<style>${markup.css}</style>${sanitizeHtml(markup.html)}`;
    document.body.append(frame);
    await new Promise<void>((resolve) => {
      frame.onload = () => resolve();
      setTimeout(resolve, 3000);
    });
    const view = frame.contentDocument;
    if (view) {
      if (view.documentElement.scrollWidth > 392)
        report('Content overflows a 390px mobile screen. Review fixed widths.');
      for (const image of view.images)
        if (image.complete && image.getAttribute('src') && !image.naturalWidth)
          report(`Image could not load: ${image.getAttribute('src')}`);
    }
    frame.remove();
  }
  return issues.filter(
    (issue, index) =>
      issues.findIndex(
        (other) => other.pageId === issue.pageId && other.detail === issue.detail,
      ) === index,
  );
}
